import React from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    Stepper,
    Step,
    StepLabel,
    DialogActions,
    Grid,
} from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { Loading } from "pages/parts/Loading";
import { ConfirmParts } from "pages/booking/dialogParts/ConfirmParts";
import { SubscribeParts } from "pages/booking/dialogParts/SubscribeParts";
import { SelectSolventParts } from "pages/booking/dialogParts/SelectSolventParts";
import { SelectBookingType } from "pages/booking/dialogParts/SelectBookingType";
import { SelectBookingTime } from "pages/booking/dialogParts/SelectBookingTime";
import { DisplayDate } from "pages/booking/dialogParts/DisplayDate";
import { BookingTypeName } from "pages/booking/dialogParts/BookingTypeName";
import { EventType } from "pages/statuses/EventType";
import { Solvent } from "pages/statuses/Solvent";
import * as CommonConstants from "utilities/CommonConstants";
import "common.css";
import { DateUtil } from "utilities/DateUtil";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: `100%`,
            position: `relative`,
        },
        instructions: {
            marginBottom: theme.spacing(1),
        },
        cancelButton: {
            marginLeft: theme.spacing(1),
        },
        loading: {
            width: `20%`,
            margin: `0 auto`,
            marginTop: 10,
            position: `absolute`,
            top: `50%`,
            left: `50%`,
        },
        closeButton: {
            position: `absolute`,
            right: theme.spacing(1),
            top: theme.spacing(1),
            color: theme.palette.grey[500],
        },
        stepLabel: {
            fontSize: `x-small`,
        },
        cautionMessage: {
            marginTop: theme.spacing(5),
            color: `red`,
            textAlign: `center`,
        },
    })
);

interface Props {
    // 開くダイアログ選択
    isOpen: boolean;
    // キャンセル
    onClickCancel: VoidFunction;
    // 登録
    onClickAdd: VoidFunction;
    // 変更
    onClickChange: VoidFunction;
    // 予約情報
    booking: EventType;
    // 次へ
    handleNext: VoidFunction;
    // 戻る
    handleBack: VoidFunction;
    // ステッパーの数値
    step: number;
    // 予約可能溶媒
    bookableSolvent: Solvent[];
    // 予約可能溶媒セッター
    setBookableSolvent: any;
    // 登録溶媒表示ボタン削除
    buttonDelete: any;
    // 予約対象溶媒
    targetBookingSolvent: Solvent[];
    // 予約対象溶媒セッター
    setTargetBookingSolvent: any;
    //全ての溶媒情報
    allSolvents: Solvent[];
    // エラーメッセージ
    errorMessage: string;
    // ローディング
    isLoading: boolean;
    // ローディング状態セッター
    setLoading: any;
    // 予約情報
    events: EventType[];
}

const getSteps = () => {
    return [`種類選択`, `時間選択`, `溶媒選択`, `予約者情報`, `内容確認`];
};

/**
 * 予約登録ダイアログコンポーネント
 * 予約情報を登録するコンポーネント
 * @param props
 */
export const SubscribeDialog: React.FC<Props> = (props) => {
    const {
        isOpen,
        onClickCancel,
        onClickAdd,
        onClickChange,
        booking,
        handleNext,
        handleBack,
        step,
        bookableSolvent,
        setBookableSolvent,
        buttonDelete,
        targetBookingSolvent,
        setTargetBookingSolvent,
        errorMessage,
        allSolvents,
        isLoading,
        setLoading,
        events,
    } = props;
    const classes = useStyles();
    const isPcSize = useMediaQuery(CommonConstants.RESPONSIVE_MIN_WIDTH);
    const steps = getSteps();

    const getStepContent = (stepIndex: number) => {
        switch (stepIndex) {
            case 0:
                return (
                    <SelectBookingType
                        booking={booking}
                        handleNext={handleNext}
                     />
                );
            case 1:
                return (
                    <SelectBookingTime
                        booking={booking}
                        events={events}
                        handleNext={handleNext}
                        setLoading={setLoading}
                    />
                );
            case 2:
                return (
                    <SelectSolventParts
                        booking={booking}
                        bookableSolvent={bookableSolvent}
                        setBookableSolvent={setBookableSolvent}
                        buttonDelete={buttonDelete}
                        targetBookingSolvent={targetBookingSolvent}
                        setTargetBookingSolvent={setTargetBookingSolvent}
                        allSolvents={allSolvents}
                        setLoading={setLoading}
                    />
                );
            case 3:
                return <SubscribeParts booking={booking} setLoading={setLoading} />;
            case 4:
                return <ConfirmParts booking={booking} />;
            default:
                return `Unknown stepIndex`;
        }
    };

    const checkStep = () => {
        const result = step === steps.length;
        return result;
    };

    // 登録・変更ボタンの判定
    const selectButton = () => {
        if (step === steps.length - 1) {
            return 0;
        }
        return 1;
    };

    const body = (
        <>
            <IconButton aria-label="close" className={classes.closeButton} onClick={onClickCancel}>
                <CloseIcon />
            </IconButton>
            <DialogTitle id="form-dialog-title">
                { step < CommonConstants.SELECT_SOLVENT_DIALOG ? (
                    <span>{DateUtil.parseViewDate(booking.start)}</span>
                ) : (
                    <DisplayDate {...props} />
                )}
                { step !== CommonConstants.SELECT_BOOKING_TYPE_DIALOG && (
                    <BookingTypeName {...props} />
                )}
                <Stepper activeStep={step} alternativeLabel>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel classes={{ label: classes.stepLabel }}>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </DialogTitle>
            <DialogContent dividers>
                <p className="error">{errorMessage}</p>
                <Loading isLoading={isLoading} />
                {checkStep() ? (
                    <div className={classes.instructions}>予約が完了しました。</div>
                ) : (
                    <>
                        <div className={classes.instructions}>{getStepContent(step)}</div>
                        <div className={classes.cautionMessage}>
                            <div>※予約はまだ完了していません※</div>
                        </div>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {checkStep() ? (
                    <Button variant="contained" color="primary" onClick={onClickCancel}>
                        OK
                    </Button>
                ) : (
                    <Grid container>
                        <Grid item xs={1} container justify="flex-start">
                            { step === CommonConstants.SELECT_BOOKING_TYPE_DIALOG ? (
                                <Button fullWidth onClick={onClickCancel} className={classes.cancelButton} color="primary">
                                    閉じる
                                </Button>
                            ) : (
                                // 予約更新時は、溶媒選択画面に戻るボタンを表示しない
                                !(booking.id !== `` && step === CommonConstants.SELECT_SOLVENT_DIALOG) && (
                                    <Button fullWidth variant="contained" color="default" onClick={handleBack}>戻る</Button>
                                )
                            )}
                        </Grid>
                        <Grid item xs={4}>
                        </Grid>
                        <Grid item xs={4}></Grid>
                        <Grid item xs={1}></Grid>
                        <Grid item xs={2}>
                            {selectButton() === 0 ? (
                                booking.id === `` ? (
                                    <Button fullWidth variant="contained" color="primary" onClick={onClickAdd}>
                                        登録
                                    </Button>
                                ) : (
                                    <Button fullWidth variant="contained" color="primary" onClick={onClickChange}>
                                        更新
                                    </Button>
                                    )
                                ) : (
                                    (step >= CommonConstants.SELECT_SOLVENT_DIALOG) && (
                                        <Button fullWidth variant="contained" color="primary" onClick={handleNext}>
                                            次へ
                                        </Button>
                                    )
                                )}
                        </Grid>
                    </Grid>
                )}
            </DialogActions>
            </>
    );

    return (
        <Dialog
            maxWidth="md"
            fullWidth={true}
            fullScreen={!isPcSize}
            open={isOpen}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            {body}
        </Dialog>
    );
};
